import React, { Component } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { removeProductFromList } from '../../../redux/actions/product';

class ProductSearchResults extends Component {
    constructor(props) {
        super(props);
        this.state = {
            show: false,
            productToDelete: null,
            // Pagination state
            currentPage: 1,
            itemsPerPage: 10,
            totalPages: 1,
            visibleProducts: [],
            // Pagination options
            paginationOptions: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
            loadingMore: false,
            // Sorting state
            sortConfig: {
                key: null,
                direction: 'ascending'
            },
            // Column visibility
            visibleColumns: {
                famille: true,
                nomCommercial: true,
                dci: true,
                dosage: true,
                forme: true,
                conditionnement: true,
                actions: true
            }
        };

        this.initializePagination = this.initializePagination.bind(this);
        this.requestSort = this.requestSort.bind(this);
        this.getSortIndicator = this.getSortIndicator.bind(this);
        this.handlePageChange = this.handlePageChange.bind(this);
        this.handleItemsPerPageChange = this.handleItemsPerPageChange.bind(this);
        this.renderTableHeaders = this.renderTableHeaders.bind(this);
        this.renderProductRow = this.renderProductRow.bind(this);
        this.renderPaginationControls = this.renderPaginationControls.bind(this);
        this.isAdmin = this.isAdmin.bind(this);
        this.setModal = this.setModal.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.removeFromList = this.removeFromList.bind(this);
    }

    componentDidMount() {
        this.initializePagination();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.products !== this.props.products) {
            this.initializePagination();
        }
    }

    initializePagination() {
        const { products } = this.props;
        const { itemsPerPage } = this.state;

        if (!products || products.length === 0) {
            this.setState({
                visibleProducts: [],
                currentPage: 1,
                totalPages: 1
            });
            return;
        }

        const totalPages = Math.ceil(products.length / itemsPerPage);
        const startIndex = 0;
        const endIndex = Math.min(itemsPerPage, products.length);
        const initialProducts = products.slice(startIndex, endIndex);

        this.setState({
            visibleProducts: initialProducts,
            currentPage: 1,
            totalPages: totalPages
        });
    }

    // Fonction de tri
    requestSort(key) {
        let direction = 'ascending';
        const { sortConfig } = this.state;

        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }

        const sortedProducts = [...this.state.visibleProducts].sort((a, b) => {
            const aValue = a[key] || '';
            const bValue = b[key] || '';

            if (aValue < bValue) {
                return direction === 'ascending' ? -1 : 1;
            }
            if (aValue > bValue) {
                return direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });

        this.setState({
            visibleProducts: sortedProducts,
            sortConfig: { key, direction }
        });
    }

    getSortIndicator(key) {
        const { sortConfig } = this.state;
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
    }

    // Gestion pagination
    handlePageChange(pageNumber) {
        const { products } = this.props;
        const { itemsPerPage } = this.state;

        if (pageNumber < 1 || pageNumber > this.state.totalPages) return;

        const startIndex = (pageNumber - 1) * itemsPerPage;
        const endIndex = Math.min(startIndex + itemsPerPage, products.length);
        const newProducts = products.slice(startIndex, endIndex);

        this.setState({
            currentPage: pageNumber,
            visibleProducts: newProducts,
            loadingMore: false
        });

        const tableElement = document.querySelector('.products-table-container');
        if (tableElement) {
            tableElement.scrollTo({ top: 0, behavior: 'auto' });
        }
    }

    handleItemsPerPageChange(itemsCount) {
        const { products } = this.props;
        const totalPages = Math.ceil(products.length / itemsCount);
        const startIndex = 0;
        const endIndex = Math.min(itemsCount, products.length);
        const initialProducts = products.slice(startIndex, endIndex);

        this.setState({
            itemsPerPage: itemsCount,
            currentPage: 1,
            totalPages: totalPages,
            visibleProducts: initialProducts
        });
    }

    renderTableHeaders() {
        const columns = [
            { key: 'familleTherapeutique', label: 'Famille', icon: 'fa-heartbeat', sortable: true },
            { key: 'nomCommercial', label: 'Nom commercial', icon: 'fa-tag', sortable: true },
            { key: 'DCI', label: 'DCI', icon: 'fa-flask', sortable: true },
            { key: 'dosage', label: 'Dosage', icon: 'fa-prescription-bottle-alt', sortable: true },
            { key: 'forme', label: 'Forme', icon: 'fa-capsules', sortable: true },
            { key: 'conditionnement', label: 'Conditionnement', icon: 'fa-box', sortable: true }
        ];

        return (
            <thead>
            <tr style={{
                background: 'var(--app-primary-color)',
                color: 'white'
            }}>
                <th style={{
                    padding: '20px 24px',
                    fontWeight: '700',
                    fontSize: '15px',
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '3px solid var(--app-primary-color)',
                    width: '60px'
                }}>
                    <i className="fa fa-hashtag" style={{ marginRight: '10px' }}></i>
                    N°
                </th>
                {columns.map(column => (
                    <th
                        key={column.key}
                        onClick={() => column.sortable && this.requestSort(column.key)}
                        style={{
                            padding: '20px 24px',
                            fontWeight: '700',
                            fontSize: '15px',
                            textAlign: 'left',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            borderBottom: '3px solid var(--app-primary-color)',
                            cursor: column.sortable ? 'pointer' : 'default',
                            position: 'relative'
                        }}
                        onMouseEnter={(e) => column.sortable && (e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)')}
                        onMouseLeave={(e) => column.sortable && (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <i className={`fa ${column.icon}`} style={{ marginRight: '10px' }}></i>
                            {column.label}
                            {column.sortable && (
                                <span style={{
                                    marginLeft: '8px',
                                    fontSize: '12px',
                                    opacity: 0.8
                                }}>
                                        {this.getSortIndicator(column.key)}
                                    </span>
                            )}
                        </div>
                    </th>
                ))}
                <th style={{
                    padding: '20px 24px',
                    fontWeight: '700',
                    fontSize: '15px',
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '3px solid var(--app-primary-color)',
                    width: '300px'
                }}>
                    <i className="fa fa-tools" style={{ marginRight: '10px' }}></i>
                    Actions
                </th>
            </tr>
            </thead>
        );
    }

    renderProductRow(product, index) {
        const globalIndex = (this.state.currentPage - 1) * this.state.itemsPerPage + index + 1;

        return (
            <tr
                key={product._id || product.id || `product-${globalIndex}`}
                style={{
                    borderBottom: '1px solid #e9ecef'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--app-primary-tint)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                }}
            >
                <td style={{
                    padding: '18px 24px',
                    fontWeight: '600',
                    color: '#2c3e50',
                    fontSize: '15px',
                    textAlign: 'center',
                    fontFamily: 'monospace'
                }}>
                    {globalIndex}
                </td>
                <td style={{
                    padding: '18px 24px',
                    fontWeight: '600',
                    color: '#2c3e50',
                    fontSize: '15px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--app-primary-color)',
                            marginRight: '12px'
                        }}></div>
                        {product.familleTherapeutique || 'N/A'}
                    </div>
                </td>
                <td style={{
                    padding: '18px 24px',
                    fontWeight: '600',
                    color: '#34495e',
                    fontSize: '15px'
                }}>
                    <div style={{
                        color: '#34495e',
                        fontWeight: '700'
                    }}>
                        {product.nomCommercial || product.nom || 'N/A'}
                    </div>
                </td>
                <td style={{
                    padding: '18px 24px',
                    color: '#5a6c7d',
                    fontSize: '14px'
                }}>
                    <span style={{
                        color: 'var(--app-primary-color)',
                        fontSize: '13px',
                        fontWeight: '500'
                    }}>
                        {product.DCI || product.dci || 'N/A'}
                    </span>
                </td>
                <td style={{
                    padding: '18px 24px',
                    color: '#5a6c7d',
                    fontSize: '14px',
                    fontWeight: '500'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <i className="fa fa-prescription-bottle-alt" style={{
                            marginRight: '8px',
                            color: '#4caf50',
                            fontSize: '16px'
                        }}></i>
                        {product.dosage || 'N/A'}
                    </div>
                </td>
                <td style={{
                    padding: '18px 24px',
                    color: '#5a6c7d'
                }}>
                    <span style={{
                        color: '#f57c00',
                        padding: '8px 14px',
                        fontSize: '13px',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}>
                        <i className="fa fa-capsules" style={{ fontSize: '12px' }}></i>
                        {product.forme || 'N/A'}
                    </span>
                </td>
                <td style={{
                    padding: '18px 24px',
                    color: '#5a6c7d',
                    fontSize: '14px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <i className="fa fa-box" style={{
                            marginRight: '8px',
                            color: '#9c27b0',
                            fontSize: '14px'
                        }}></i>
                        {product.conditionnement || 'N/A'}
                    </div>
                </td>
                <td style={{
                    padding: '18px 24px',
                    textAlign: 'center'
                }}>
                    <div style={{
                        display: 'flex',
                        gap: '4px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexWrap: 'nowrap',
                        minWidth: '260px'
                    }}>
                        {(() => {
                            const productId = product._id || product.id;

                            if (!productId || productId === 'unknown') {
                                return (
                                    <Button
                                        bsStyle="default"
                                        bsSize="xs"
                                        disabled
                                        style={{
                                            borderRadius: '6px',
                                            padding: '6px 12px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            minWidth: '70px',
                                            opacity: 0.5
                                        }}
                                        title="ID médicament manquant"
                                    >
                                        <i className="fa fa-eye"></i>
                                    </Button>
                                );
                            }

                            return (
                                <Link to={`/app/product/detail/${productId}`}>
                                    <Button
                                        bsStyle="info"
                                        bsSize="xs"
                                        style={{
                                            borderRadius: '6px',
                                            padding: '6px 10px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            minWidth: '75px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            justifyContent: 'center'
                                        }}
                                        title={`Voir les détails (ID: ${productId})`}
                                        onClick={() => {
                                        }}
                                    >
                                        <i className="fa fa-eye"></i>
                                        <span>Détail</span>
                                    </Button>
                                </Link>
                            );
                        })()}

                        {this.isAdmin() && (
                            (() => {
                                const productId = product._id || product.id;

                                if (!productId || productId === 'unknown') {
                                    return (
                                        <Button
                                            bsStyle="default"
                                            bsSize="xs"
                                            disabled
                                            style={{
                                                borderRadius: '6px',
                                                padding: '6px 12px',
                                                fontSize: '11px',
                                                fontWeight: '600',
                                                minWidth: '70px',
                                                opacity: 0.5
                                            }}
                                            title="ID médicament manquant"
                                        >
                                            <i className="fa fa-edit"></i>
                                        </Button>
                                    );
                                }

                                return (
                                    <Link to={`/app/product/form/${productId}`}>
                                        <Button
                                            bsStyle="warning"
                                            bsSize="xs"
                                            style={{
                                                borderRadius: '6px',
                                                padding: '6px 12px',
                                                fontSize: '11px',
                                                fontWeight: '600',
                                                minWidth: '80px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}
                                            title={`Modifier le médicament (ID: ${productId})`}
                                            onClick={() => {
                                            }}
                                        >
                                            <i className="fa fa-edit"></i>
                                            <span>Editer</span>
                                        </Button>
                                    </Link>
                                );
                            })()
                        )}

                        <Button
                            bsStyle="danger"
                            bsSize="xs"
                            onClick={() => this.setModal(product)}
                            style={{
                                borderRadius: '6px',
                                padding: '6px 12px',
                                fontSize: '11px',
                                fontWeight: '600',
                                minWidth: '80px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                            title="Retirer de la liste"
                        >
                            <i className="fa fa-trash"></i>
                            <span>Supprimer</span>
                        </Button>
                    </div>
                </td>
            </tr>
        );
    }

    // Méthode pour le rendu des contrôles de pagination
    renderPaginationControls() {
        const { currentPage, totalPages, itemsPerPage, paginationOptions } = this.state;
        const { products } = this.props;

        if (!products || products.length <= 10) return null;

        const startItem = (currentPage - 1) * itemsPerPage + 1;
        const endItem = Math.min(currentPage * itemsPerPage, products.length);

        return (
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '20px',
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                flexWrap: 'wrap',
                gap: '15px'
            }}>
                {/* Informations de pagination */}
                <div style={{
                    fontSize: '14px',
                    color: '#6c757d',
                    fontWeight: '500'
                }}>
                    Affichage de {startItem} à {endItem} sur {products.length} médicaments
                </div>

                {/* Contrôles de pagination */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {/* Sélecteur d'éléments par page */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '14px', color: '#6c757d' }}>Afficher:</span>
                        <select
                            value={itemsPerPage}
                            onChange={(e) => this.handleItemsPerPageChange(parseInt(e.target.value))}
                            style={{
                                padding: '6px 10px',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                fontSize: '14px',
                                backgroundColor: 'white'
                            }}
                        >
                            {paginationOptions.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>

                    {/* Boutons de navigation */}
                    <div style={{ display: 'flex', gap: '5px' }}>
                        <button
                            onClick={() => this.handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            style={{
                                padding: '8px 12px',
                                border: '1px solid #dee2e6',
                                backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
                                color: currentPage === 1 ? '#6c757d' : '#495057',
                                borderRadius: '4px',
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            <i className="fa fa-chevron-left"></i>
                        </button>

                        {/* Numéros de page */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (currentPage <= 3) {
                                pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                            } else {
                                pageNum = currentPage - 2 + i;
                            }

                            return (
                                <button
                                    key={pageNum}
                                    onClick={() => this.handlePageChange(pageNum)}
                                    style={{
                                        padding: '8px 12px',
                                        border: '1px solid #dee2e6',
                                        backgroundColor: pageNum === currentPage ? 'var(--app-primary-color)' : 'white',
                                        color: pageNum === currentPage ? 'white' : '#495057',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: pageNum === currentPage ? '600' : 'normal'
                                    }}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}

                        <button
                            onClick={() => this.handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            style={{
                                padding: '8px 12px',
                                border: '1px solid #dee2e6',
                                backgroundColor: currentPage === totalPages ? '#f8f9fa' : 'white',
                                color: currentPage === totalPages ? '#6c757d' : '#495057',
                                borderRadius: '4px',
                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            <i className="fa fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { products, loading } = this.props;
        const { visibleProducts, productToDelete } = this.state;

        return (
            <div className="products-table-container" style={{ padding: '20px' }}>
                {/* Header avec statistiques et actions */}
                {products && products.length > 0 && (
                    <div style={{
                        marginBottom: '25px',
                        padding: '20px 25px',
                        borderRadius: '16px',
                        border: '1px solid #e9ecef'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '15px'
                        }}>
                            <div>
                                <h4 style={{
                                    margin: 0,
                                    color: 'var(--app-primary-color)',
                                    fontWeight: '700',
                                    fontSize: '20px'
                                }}>
                                    <i className="fa fa-check-circle" style={{ marginRight: '12px', fontSize: '22px' }}></i>
                                    {products.length} médicament{products.length > 1 ? 's' : ''} trouvé{products.length > 1 ? 's' : ''}
                                </h4>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    marginTop: '8px',
                                    flexWrap: 'wrap'
                                }}>
                                    <small style={{
                                        fontSize: '14px',
                                        opacity: 0.9,
                                        fontWeight: 'normal',
                                        color: '#6c757d'
                                    }}>
                                        {visibleProducts.length} médicament{visibleProducts.length > 1 ? 's' : ''} affiché{visibleProducts.length > 1 ? 's' : ''}
                                    </small>

                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tableau des médicaments */}
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                    border: '1px solid #e9ecef',
                    marginBottom: '20px'
                }}>
                    <div style={{
                        overflowX: 'auto',
                        maxHeight: '600px',
                        overflowY: 'auto'
                    }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '14px',
                            minWidth: '1100px'
                        }}>
                            {this.renderTableHeaders()}
                            <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="8" style={{
                                        textAlign: 'center',
                                        padding: '60px 20px',
                                        color: '#6c757d',
                                        fontSize: '16px',
                                        fontStyle: 'italic',
                                        backgroundColor: '#f8f9fa'
                                    }}>
                                        <div>
                                            <i className="fa fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
                                            <br />
                                            Recherche en cours...
                                        </div>
                                    </td>
                                </tr>
                            ) : visibleProducts.length === 0 ? (
                                <tr>
                                    <td colSpan="8" style={{
                                        textAlign: 'center',
                                        padding: '60px 20px',
                                        color: '#6c757d',
                                        fontSize: '16px',
                                        fontStyle: 'italic',
                                        backgroundColor: '#f8f9fa'
                                    }}>
                                        <div>
                                            <i className="fa fa-search" style={{ fontSize: '24px', marginBottom: '10px', color: '#adb5bd' }}></i>
                                            <br />
                                            Aucun médicament trouvé
                                            <br />
                                            <small style={{ color: '#adb5bd' }}>Essayez de modifier vos critères de recherche</small>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                visibleProducts.map((product, index) =>
                                    this.renderProductRow(product, index)
                                )
                            )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination */}
                {products && products.length > 10 && this.renderPaginationControls()}

                {/* Modal de confirmation */}
                <Modal show={this.state.show} onHide={this.closeModal} centered>
                    <Modal.Header
                        closeButton
                        style={{
                            color: '#dc3545',
                            border: '1px solid #dc3545',
                            borderRadius: '8px 8px 0 0'
                        }}
                    >
                        <Modal.Title style={{ fontWeight: '600' }}>
                            <i className="fa fa-trash" style={{ marginRight: '10px' }}></i>
                            Retirer de la liste
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{
                        padding: '30px',
                        textAlign: 'center'
                    }}>
                        <div style={{ marginBottom: '20px' }}>
                            <i className="fa fa-exclamation-triangle" style={{
                                fontSize: '48px',
                                color: '#dc3545',
                                marginBottom: '15px'
                            }}></i>
                        </div>
                        <h5 style={{
                            color: '#495057',
                            fontWeight: '600',
                            marginBottom: '10px'
                        }}>
                            Confirmer la suppression
                        </h5>
                        <p style={{
                            fontSize: '16px',
                            fontWeight: '500',
                            color: '#dc3545',
                            margin: '15px 0'
                        }}>
                            "{productToDelete?.nom || productToDelete?.nomCommercial || 'Médicament sélectionné'}"
                        </p>
                        <p style={{ color: '#6c757d', fontSize: '14px' }}>
                            Cette action retirera le médicament uniquement de cette liste.
                            <br />
                            Le médicament restera disponible dans la base de données.
                        </p>
                    </Modal.Body>
                    <Modal.Footer style={{
                        justifyContent: 'center',
                        gap: '15px',
                        padding: '20px 30px',
                        backgroundColor: 'white',
                        border: 'none'
                    }}>
                        <Button
                            onClick={this.closeModal}
                            style={{
                                backgroundColor: '#6c757d',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 25px',
                                fontWeight: '500',
                                minWidth: '100px'
                            }}
                        >
                            <i className="fa fa-times" style={{ marginRight: '8px' }}></i>
                            Annuler
                        </Button>
                        <Button
                            onClick={() => {
                                if (productToDelete) {
                                    this.removeFromList(productToDelete);
                                }
                                this.closeModal();
                            }}
                            style={{
                                backgroundColor: '#dc3545',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 25px',
                                fontWeight: '500',
                                minWidth: '100px'
                            }}
                        >
                            <i className="fa fa-check" style={{ marginRight: '8px' }}></i>
                            Confirmer
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }

    // Méthodes utilitaires
    isAdmin() {
        const { profile } = this.props;
        return profile && (profile.role === "super" || profile.role === "admin");
    }

    setModal(product) {
        this.setState({ show: true, productToDelete: product });
    }

    closeModal() {
        this.setState({ show: false, productToDelete: null });
    }

    removeFromList(productToRemove) {
        if (this.props.removeProductFromList) {
            this.props.removeProductFromList(productToRemove.id);
            this.setState(prevState => ({
                visibleProducts: prevState.visibleProducts.filter(
                    prod => prod.id !== productToRemove.id
                )
            }));
            if (typeof window !== 'undefined' && window.showNotification) {
                window.showNotification('Médicament retiré de la liste avec succès', 'success');
            }
        }
    }
}

ProductSearchResults.propTypes = {
    products: PropTypes.array,
    profile: PropTypes.object,
    loading: PropTypes.bool,
    removeProductFromList: PropTypes.func
};

const mapStateToProps = state => ({
    products: state.products.products,
    profile: state.user.profile || state.auth.profile
});

const mapDispatchToProps = dispatch => ({
    removeProductFromList: (productId) => dispatch(removeProductFromList(productId))
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductSearchResults);